import { existsSync } from 'fs';
import { basename, join } from 'path';
import {
  DATABASE_META_FILE,
  findEnclosingFolder,
  folderForSelector,
  isProductionFolder,
  listDatabaseFolders,
  readMeta,
  type Workspace,
} from './workspace.js';

export interface ResolvedTarget {
  /** Absolute path to the database folder (may not exist yet for a first pull). */
  folder: string;
  /** Whether the folder already has a database.yaml. */
  exists: boolean;
}

/**
 * Resolve which database folder a pull/push targets. Precedence: explicit selector → enclosing
 * folder (cwd inside one) → repo `default_database` → the sole folder if there's exactly one.
 * Throws an actionable error when ambiguous or missing.
 */
export function resolveTargetFolder(ws: Workspace, selector: string | undefined, repoDefault?: string): ResolvedTarget {
  if (selector) {
    const folder = folderForSelector(ws, selector);
    return { folder, exists: existsSync(join(folder, DATABASE_META_FILE)) };
  }

  const enclosing = findEnclosingFolder(ws);
  if (enclosing) return { folder: enclosing.folder, exists: true };

  if (repoDefault) {
    const folder = join(ws.databasesDir, repoDefault);
    return { folder, exists: existsSync(join(folder, DATABASE_META_FILE)) };
  }

  // Ignore read-only production mirrors: a lone preview + its mirror must still resolve to the preview.
  const all = listDatabaseFolders(ws).filter((d) => !isProductionFolder(d.meta));
  if (all.length === 1) return { folder: all[0]!.folder, exists: true };
  throw new Error(
    all.length === 0
      ? 'No editable preview folder found under rekor-ws/databases. Pass a preview id/folder (e.g. `rekor pull <prod>--dev`).'
      : 'Multiple preview folders — say which with a positional argument or --database.',
  );
}

/**
 * The database id a target resolves to: the folder's `database_id` if it exists, otherwise the
 * selector itself (first pull of a not-yet-tracked preview). Returns null when neither is known.
 */
export function targetDatabaseId(target: ResolvedTarget, selector: string | undefined): string | null {
  if (target.exists) {
    const meta = readMeta(target.folder);
    if (meta?.database_id) return meta.database_id;
  }
  if (selector && !selector.includes('/')) return selector;
  // A path selector with no tracked id — fall back to the folder basename.
  return target.exists ? null : basename(target.folder);
}
